import React, { useState, useEffect } from 'react';
import createCtx from './createContext';
import {
  useFetchTWDataCardInterface,
  useFetchTWDataUserInterface,
  useFetchTWDataInterface,
  getAllTypesInterface,
  holidayStatusesInterface,
  contextInterface,
  authInterface,
  UseSpreadsheetsReturnType,
} from '../types/stateContextTypes';

const [useStateContext, StateProv] = createCtx<contextInterface>();

const StateProvider: React.FC = ({ children }) => {
  const [auth, setAuth] = useState<authInterface>({ requestAccessToken: () => {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>('');
  const [process, setProcess] = useState<string | null>('');

  return (
    <StateProv value={{ auth, setAuth, loading, setLoading, error, setError, process, setProcess }}>
      {children}
    </StateProv>
  );
};

const useAuth = () => {
  const context = useStateContext();
  const { auth, setAuth } = context;
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [token, setToken] = useState<string>('');

  useEffect(() => {
    if (token) {
      setIsSignedIn(true);
    }
  }, [auth, isSignedIn, token]);

  const signIn = () => {
    if (!auth) {
      return;
    }
    auth.requestAccessToken!();
    loadGapiClient();
  };

  const signOut = () => {
    if (!auth) {
      return;
    }
    google.accounts.oauth2.revoke(token, () => {
      setToken('');
      setIsSignedIn(false);
    });
  };

  return { setAuth, isSignedIn, signIn, signOut, token, setToken };
};

function loadGapiClient() {
  gapi.load('client', async () => {
    await gapi.client.load('https://sheets.googleapis.com/$discovery/rest?version=v4');
  });
}

const useFetchTWData = <
  T extends useFetchTWDataInterface | useFetchTWDataCardInterface | useFetchTWDataUserInterface,
>() => {
  const context = useStateContext();
  const { setLoading, setError, setProcess } = context;
  const load = (endpoint: string, params: Record<string, string | number>, propName: string): Promise<T[]> => {
    return new Promise(async (resolve, reject) => {
      setError('');
      setProcess('Fetching Teamwork data');
      setLoading(true);
      try {
        let data: T[] = [];
        let currentPage = 1;
        const url = new URL(`https://agiledrop.teamwork.com/${endpoint}`);
        while (true) {
          url.search = new URLSearchParams({ ...params, page: currentPage.toString() }).toString();
          const basicAuthKey = btoa(`${process.env.REACT_APP_API_KEY}:x`);
          const response = await fetch(url, {
            headers: {
              Authorization: `Basic ${basicAuthKey}`,
            },
          });
          const jsonData = await response.json();
          data.push(...jsonData[propName]);
          currentPage = Number(response.headers.get('x-page'));
          const totalPages = Number(response.headers.get('x-pages'));
          if (currentPage === totalPages) {
            break;
          }
          currentPage++;
        }
        resolve(data);
      } catch (error) {
        const msg = (error as Error).message || 'Something went wrong while fetching Teamwork data';
        setError(msg);
        reject(msg);
      } finally {
        setLoading(false);
        setProcess(null);
      }
    });
  };
  return { load };
};

const useFetchDasboardData = () => {
  const DASHBOARD_API_BASE_URL = 'https://api.dashboard.agiledrop.com/api';

  const context = useStateContext();
  const { setLoading, setError, setProcess } = context;
  const { token } = useAuth();

  const dashboardFetch = (
    endpoint?: string,
    params?: URLSearchParams | null,
    options?: { method?: string; body?: string },
  ) => {
    return new Promise<(getAllTypesInterface | holidayStatusesInterface)[]>(async (resolve, reject) => {
      setError('');
      setProcess('Fetching Dashboard data');
      setLoading(true);
      try {
        const url = new URL(`${DASHBOARD_API_BASE_URL}${endpoint}`);
        if (params) {
          url.search = params.toString();
        }
        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          ...options,
        });
        const data = await response.json();
        resolve(data);
      } catch (error) {
        const msg = (error as Error).message || 'Something went wrong while fetching Dashboard data';
        setError(msg);
        reject(msg);
      } finally {
        setLoading(false);
        setProcess(null);
      }
    });
  };

  return { dashboardFetch };
};

const useSpreadsheets = (spreadsheetId: string, spreadsheetName: string): UseSpreadsheetsReturnType => {
  const context = useStateContext();
  const { setLoading, setError, setProcess } = context;

  const update = (values?: (string | number | undefined)[][]) => {
    return new Promise<void>(async (resolve, reject) => {
      setError('');
      setProcess('Updating spreadsheet');
      setLoading(true);
      try {
        await gapi.client.sheets.spreadsheets.values.update({
          spreadsheetId: spreadsheetId,
          range: spreadsheetName,
          valueInputOption: 'USER_ENTERED',
          resource: {
            range: spreadsheetName,
            majorDimension: 'ROWS',
            values,
          },
        });
        resolve();
      } catch (error) {
        const msg = (error as Error).message || 'Something went wrong while updating spreadsheet';
        setError(msg);
        reject(msg);
      } finally {
        setLoading(false);
        setProcess(null);
      }
    });
  };

  const clear = () => {
    return new Promise<void>(async (resolve, reject) => {
      setError('');
      setProcess('Clearing spreadsheet');
      setLoading(true);
      try {
        await window.gapi.client.sheets.spreadsheets.values.clear({
          spreadsheetId: spreadsheetId,
          range: spreadsheetName,
          resource: {},
        });
        resolve();
      } catch (error) {
        const msg = (error as Error).message || 'Something went wrong while clearing spreadsheet';
        setError(msg);
        reject(msg);
      } finally {
        setLoading(false);
        setProcess(null);
      }
    });
  };

  const format = (requests?: gapi.client.sheets.Request[]) => {
    return new Promise<void>(async (resolve, reject) => {
      setError('');
      setProcess('Formating spreadsheet');
      setLoading(true);
      try {
        await gapi.client.sheets.spreadsheets.batchUpdate({
          spreadsheetId: spreadsheetId,
          resource: {
            requests,
          },
        });
        resolve();
      } catch (error) {
        const msg = (error as Error).message || 'Something went wrong while formating spreadsheet';
        setError(msg);
        reject(msg);
      } finally {
        setLoading(false);
        setProcess(null);
      }
    });
  };

  return [update, clear, format];
};

const useLoading = () => {
  const context = useStateContext();
  const { loading, setLoading } = context;

  return [loading, setLoading];
};

const useError = () => {
  const { error, setError } = useStateContext();
  return { error, setError };
};

const useProcess = () => {
  const { process, setProcess } = useStateContext();
  return { process, setProcess };
};

export {
  StateProvider,
  useAuth,
  useLoading,
  useError,
  useProcess,
  useFetchDasboardData,
  useFetchTWData,
  useSpreadsheets,
};
